import { gql } from '@apollo/client';

import {
  WITH_COUNT_VARIABLES,
  WITH_COUNT_ARGUMENTS
} from 'app/src/queries/withCount';

import {
  TXN_FIELDS,
  TXN_LINE_FIELDS,
  TXN_ACCOUNT_FIELDS,
} from '../Txns/queries';

const ERRORS = `errors { path, message }`;

const INVOICE_SELECT_FEE_FIELDS = gql`
  fragment InvoiceSelectFeeFields on Fee {
    id
    type
    description
    amount
    asset {
      id
      stockNumber
      vin
      webPath
    }
    assetId
    saleAssetId
    invoiceOffsetAccountId
  }
`;

const INVOICE_SELECT_SALE_ASSET_FIELDS = gql`
  fragment InvoiceSelectSaleAssetFields on SaleAsset {
    id
    lotNumber
    soldOrIfPrice
    sellerPrice
    buyerFees
    buyerNet
    sellerFees
    sellerNet
    sale { saleOn }
    asset {
      id
      stockNumber
      vin
      webPath
      fees {
        id
        type
        description
        buyerFee
        amount
        invoiceOffsetAccountId
        invoiceOffsetLineId
      }
    }
    assetSellerAccount { id }
  }
`;

export const INVOICE = gql`
  ${TXN_FIELDS}
  ${TXN_LINE_FIELDS}
  ${INVOICE_SELECT_SALE_ASSET_FIELDS}
  ${INVOICE_SELECT_FEE_FIELDS}
  query Txn($id: ID!) {
    txn(id: $id) {
      ...TxnFields
      txnLines {
        ...TxnLineFields
        txn { bankDate }
      }
      invoiceSaleAssets {
        soldAmount
        totalAmount
        saleAsset {
          id
          lotNumber
          asset {
            id
            stockNumber
            vin
            webPath
            name
          }
          sale {
            saleOn
          }
        }
        fees {
          amount
          fee {
            id
            type
            description
          }
        }
      }
      invoiceAddableSaleAssets {
        ...InvoiceSelectSaleAssetFields
      }
      invoiceAddableFees {
        ...InvoiceSelectFeeFields
      }
      invoiceAddableCreditMemoLines {
        ...TxnLineFields
        amount
        openBalance
        txn {
          ...TxnFields
        }
      }
      openBalance
      offsettingTxns {
        id
        bankDate
        amount
      }
      correspondence {
        id
        createdAt
        recipientList
        userLog {
          id
          user { firstName, lastName }
        }
        attachment {
          id
          filename
          url
        }
      }
    }
  }
`;

export const INVOICES_WITH_COUNT = gql`
  ${TXN_FIELDS}
  ${TXN_LINE_FIELDS}
  ${TXN_ACCOUNT_FIELDS}
  query (
    $assetId: ID
    ${WITH_COUNT_VARIABLES}
  ) {
    txnsWithCount(
      assetId: $assetId
      ${WITH_COUNT_ARGUMENTS}
    ) {
      entries {
        ...TxnFields
        openBalance
        offsettingTxns {
          id
          bankDate
          amount
        }
        sales {
          id
          name
          saleOn
        }
        txnLines {
          ...TxnLineFields
        }
        createdBy {
          fullName
        }
      }
      accounts {
        ...TxnAccountFields
      }
      types
      count
    }
  }
`;


export const INVOICE_SELECTS = gql`
  ${INVOICE_SELECT_SALE_ASSET_FIELDS}
  ${INVOICE_SELECT_FEE_FIELDS}
  ${TXN_FIELDS}
  ${TXN_LINE_FIELDS}
  query NoUserLogInvoiceSelects(
    $saleId: ID
    $entityType: String
    $entityId: ID
    $saleAssetScopes: [String!]!
  ) {
    fees (
      scopes: ["ready_to_invoice"]
      entityType: $entityType
      entityId: $entityId
    ) {
      ...InvoiceSelectFeeFields
    }
    saleAssets (
      scopes: $saleAssetScopes
      entityType: $entityType
      entityId: $entityId
      saleId: $saleId
    ) {
      ...InvoiceSelectSaleAssetFields
    }
    creditMemoLines: txnLines(
      scopes: ["unused_credit_memo"]
      entityType: $entityType
      entityId: $entityId
    ) {
      ...TxnLineFields
      amount
      openBalance
      txn {
        ...TxnFields
      }
    }
    settings {
      nextInvoiceNumber
      processingFee
    }
  }
`;

export const EMAIL_INVOICE = gql`
  mutation EmailInvoice (
    $txnId: ID!
    $to: String!
    $cc: String
  ) {
    emailInvoice (
      txnId: $txnId
      to: $to
      cc: $cc
    ) {
      txn {
        id
      }
      ${ERRORS}
    }
  }
`;

export const REMOVE_SALE_ASSET_FROM_INVOICE = gql`
  mutation RemoveSaleAssetFromInvoice (
    $saleAssetId: ID!
    $txnId: ID!
  ) {
    removeSaleAssetFromInvoice(
      saleAssetId: $saleAssetId
      txnId: $txnId
    ) {
      ${ERRORS}
    }
  }
`;

export const ADD_SALE_ASSETS_TO_INVOICE = gql`
  mutation AddSaleAssetsToInvoice (
    $saleAssetIds: [ID!]!
    $txnId: ID!
  ) {
    addSaleAssetsToInvoice(
      saleAssetIds: $saleAssetIds
      txnId: $txnId
    ) {
      ${ERRORS}
    }
  }
`;

export const ADD_FEES_TO_INVOICE = gql`
  mutation AddFeesToInvoice (
    $feeIds: [ID!]!
    $txnId: ID!
  ) {
    addFeesToInvoice(
      feeIds: $feeIds
      txnId: $txnId
    ) {
      ${ERRORS}
    }
  }
`;

export const ADD_CREDIT_MEMOS_TO_INVOICE = gql`
  mutation AddCreditMemosToInvoice (
    $creditLineAmounts: [OffsetAmountInput!]!
    $txnId: ID!
  ) {
    addCreditMemosToInvoice(
      creditLineAmounts: $creditLineAmounts
      txnId: $txnId
    ) {
      ${ERRORS}
    }
  }
`;
